<template>
  <v-container>
    <v-layout row><v-flex>
      <v-card-text>
        <v-container>
          <div class="as-head">
            <h2>Add-on Services</h2>
            <div class="as-head-actions">
              <v-btn small color="primary" @click="openAdd">+ Add signup</v-btn>
              <v-btn small outlined @click="openCsvImport">📋 Paste CSV</v-btn>
              <v-btn small outlined @click="openCarpoolImport">🚗 Import from carpools</v-btn>
            </div>
          </div>
          <p class="muted small">
            Photoshoots ($85–$200) · Physical Therapy ($50/25 min) · Private Lessons ($50/30 min).
            Welcome-gift photoshoot credit ($85) applies per photoshoot gift on the dancer record (2026 + 2027 labels).
            Card on file charged via Stripe when you click <strong>Charge</strong>, or
            click <strong>📧 Send invoice</strong> to email a hosted Stripe invoice the dancer pays herself — no card charge.
          </p>

          <div class="as-summary">
            <div class="as-stat">
              <div class="as-stat-num">{{ pendingRows.length }}</div>
              <div class="as-stat-lab">Pending</div>
            </div>
            <div class="as-stat">
              <div class="as-stat-num">{{ paidRows.length }}</div>
              <div class="as-stat-lab">Paid</div>
            </div>
            <div class="as-stat">
              <div class="as-stat-num">${{ (pendingTotalCents / 100).toFixed(2) }}</div>
              <div class="as-stat-lab">Total to charge</div>
            </div>
          </div>

          <div v-if="pendingDancerCount > 0" class="as-bulk">
            <v-btn small color="success" :loading="bulkCharging" @click="chargeAllPending">
              💳 Charge all {{ pendingDancerCount }} dancer{{ pendingDancerCount === 1 ? '' : 's' }} (${{ (pendingTotalCents / 100).toFixed(2) }})
            </v-btn>
            <span class="muted small">
              {{ pendingRows.length }} service{{ pendingRows.length === 1 ? '' : 's' }} across {{ pendingDancerCount }} charge{{ pendingDancerCount === 1 ? '' : 's' }}
            </span>
            <span v-if="bulkProgress" class="muted small">{{ bulkProgress }}</span>
          </div>

          <details v-if="photoshootPnl.signupCount || carpoolPnl.signupCount" class="as-pnl">
            <summary>
              <strong>📊 P&amp;L — photoshoots &amp; carpools</strong>
              <span class="muted small">
                — click to expand
              </span>
            </summary>
            <div class="pnl-grid">
              <div v-if="photoshootPnl.signupCount" class="pnl-card">
                <div class="pnl-title">📸 Photoshoots ({{ photoshootPnl.signupCount }} signup{{ photoshootPnl.signupCount === 1 ? '' : 's' }})</div>
                <div class="pnl-row">
                  <span>Charged to dancers</span>
                  <strong>${{ (photoshootPnl.chargedCents / 100).toFixed(2) }}</strong>
                </div>
                <div class="pnl-row pnl-row-muted">
                  <span>Gift value given</span>
                  <span>−${{ (photoshootPnl.giftCents / 100).toFixed(2) }}</span>
                </div>
                <div class="pnl-row pnl-row-input">
                  <label>You spent</label>
                  <div class="pnl-input-wrap">
                    <span class="pnl-dollar">$</span>
                    <input
                      type="number" step="0.01" min="0"
                      :value="pnlSpendDraft.photoshoot"
                      @input="onPnlSpendInput('photoshoot', $event.target.value)"
                      @blur="savePnlSpend('photoshoot')"
                      class="pnl-input"
                      placeholder="0.00"
                    />
                    <span v-if="pnl.savedFlash === 'photoshoot'" class="pnl-flash">✓</span>
                  </div>
                </div>
                <div class="pnl-row pnl-row-total" :class="photoshootPnl.netCents < 0 ? 'neg' : 'pos'">
                  <span>Net</span>
                  <strong>
                    {{ photoshootPnl.netCents < 0 ? '−$' : '$' }}{{ (Math.abs(photoshootPnl.netCents) / 100).toFixed(2) }}
                  </strong>
                </div>
              </div>

              <div v-if="carpoolPnl.signupCount" class="pnl-card">
                <div class="pnl-title">🚗 Carpools ({{ carpoolPnl.signupCount }} signup{{ carpoolPnl.signupCount === 1 ? '' : 's' }})</div>
                <div class="pnl-row">
                  <span>Charged to dancers</span>
                  <strong>${{ (carpoolPnl.chargedCents / 100).toFixed(2) }}</strong>
                </div>
                <div class="pnl-row pnl-row-muted">
                  <span>Gift value given</span>
                  <span>−${{ (carpoolPnl.giftCents / 100).toFixed(2) }}</span>
                </div>
                <div class="pnl-row pnl-row-input">
                  <label>You spent</label>
                  <div class="pnl-input-wrap">
                    <span class="pnl-dollar">$</span>
                    <input
                      type="number" step="0.01" min="0"
                      :value="pnlSpendDraft.carpool"
                      @input="onPnlSpendInput('carpool', $event.target.value)"
                      @blur="savePnlSpend('carpool')"
                      class="pnl-input"
                      placeholder="0.00"
                    />
                    <span v-if="pnl.savedFlash === 'carpool'" class="pnl-flash">✓</span>
                  </div>
                </div>
                <div class="pnl-row pnl-row-total" :class="carpoolPnl.netCents < 0 ? 'neg' : 'pos'">
                  <span>Net</span>
                  <strong>
                    {{ carpoolPnl.netCents < 0 ? '−$' : '$' }}{{ (Math.abs(carpoolPnl.netCents) / 100).toFixed(2) }}
                  </strong>
                </div>
              </div>
            </div>
          </details>

          <details v-if="photoshootRoster.length" class="as-roster">
            <summary>
              <strong>📸 Photoshoot roster (per dancer)</strong>
              <span class="muted small">
                — {{ photoshootRosterTotals.dancers }} dancer{{ photoshootRosterTotals.dancers === 1 ? '' : 's' }},
                {{ photoshootRosterTotals.totalShots }} total shots across
                {{ photoshootRosterTotals.indoorSlots + photoshootRosterTotals.outdoorSlots }} slot{{ (photoshootRosterTotals.indoorSlots + photoshootRosterTotals.outdoorSlots) === 1 ? '' : 's' }}
                <span v-if="photoshootRosterTotals.outdoorSlots > 0"> ({{ photoshootRosterTotals.indoorSlots }} indoor / {{ photoshootRosterTotals.outdoorSlots }} outdoor)</span>
              </span>
            </summary>
            <p class="muted small">
              Alphabetical list of every dancer with a photoshoot signup. Dancers can choose to
              weight their final edits toward indoor or outdoor — <strong>Total Shots</strong> is
              the count they're entitled to, split across the slots however they prefer.
            </p>
            <ul class="roster-list">
              <li v-for="r in photoshootRoster" :key="r.dancerName">
                <strong>{{ r.dancerName }}</strong>
                <span class="muted">
                  — {{ r.totalShots }} total shot{{ r.totalShots === 1 ? '' : 's' }} across
                  <span v-if="r.indoorSlots > 0">{{ r.indoorSlots }} indoor</span>
                  <span v-if="r.indoorSlots > 0 && r.outdoorSlots > 0"> + </span>
                  <span v-if="r.outdoorSlots > 0">{{ r.outdoorSlots }} outdoor</span>
                  slot{{ (r.indoorSlots + r.outdoorSlots) === 1 ? '' : 's' }}
                  <span v-if="r.rawProofs">· + all raw proofs</span>
                </span>
              </li>
            </ul>
            <div class="roster-actions">
              <v-btn x-small outlined @click="copyPhotoshootRoster">
                {{ payoutCopyFlash['__roster__'] ? '✓ Copied' : '📋 Copy list' }}
              </v-btn>
              <v-btn x-small outlined @click="downloadPhotoshootRosterCsv">↓ Download CSV</v-btn>
            </div>
          </details>

          <details v-if="payouts.length" class="as-payouts">
            <summary>
              <strong>💰 Payouts to teachers &amp; PT</strong>
              <span class="muted small">
                — {{ payouts.length }} payee{{ payouts.length === 1 ? '' : 's' }},
                <strong>${{ (payoutsTotals.collectedCents / 100).toFixed(2) }}</strong> ready to send
                <span v-if="payoutsTotals.uncollectedCents > 0">
                  · ${{ (payoutsTotals.uncollectedCents / 100).toFixed(2) }} still pending charge
                </span>
              </span>
            </summary>
            <p class="muted small">
              Photoshoots skipped (Yoshi &amp; Spencer are covered separately). Numbers below
              are the $ collected from dancers so far — that's what you send each teacher.
            </p>
            <table class="as-table sm">
              <thead>
                <tr>
                  <th>Teacher / therapist</th>
                  <th class="num">Sessions</th>
                  <th class="num">Ready to pay out</th>
                  <th class="num">Uncollected</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="p in payouts" :key="p.name">
                  <td>{{ p.name }}</td>
                  <td class="num">{{ p.sessions }}</td>
                  <td class="num"><strong>${{ (p.collectedCents / 100).toFixed(2) }}</strong></td>
                  <td class="num muted">
                    {{ p.uncollectedCents > 0 ? '$' + (p.uncollectedCents / 100).toFixed(2) : '—' }}
                  </td>
                  <td>
                    <v-btn x-small text @click="copyPayoutLine(p)">
                      {{ payoutCopyFlash[p.name] ? '✓' : '📋' }}
                    </v-btn>
                  </td>
                </tr>
                <tr class="payout-totals">
                  <td><strong>Total</strong></td>
                  <td class="num"><strong>{{ payoutsTotals.sessions }}</strong></td>
                  <td class="num"><strong>${{ (payoutsTotals.collectedCents / 100).toFixed(2) }}</strong></td>
                  <td class="num muted">
                    {{ payoutsTotals.uncollectedCents > 0 ? '$' + (payoutsTotals.uncollectedCents / 100).toFixed(2) : '—' }}
                  </td>
                  <td>
                    <v-btn x-small outlined @click="copyPayoutList">
                      {{ payoutCopyFlash['__all__'] ? '✓ Copied' : 'Copy all' }}
                    </v-btn>
                  </td>
                </tr>
              </tbody>
            </table>
          </details>

          <div class="as-filters">
            <label>
              Service
              <select v-model="filterService" class="as-input">
                <option value="">All services</option>
                <option value="photoshoot">Photoshoot</option>
                <option value="pt">Physical Therapy</option>
                <option value="privateLesson">Private Lesson</option>
                <option value="carpool">Airport Carpool</option>
              </select>
            </label>
            <label>
              Status
              <select v-model="filterStatus" class="as-input">
                <option value="">All</option>
                <option value="pending">Pending</option>
                <option value="paid">Paid</option>
                <option value="failed">Failed</option>
                <option value="invoiced">Invoiced</option>
              </select>
            </label>
          </div>

          <div v-if="loading" class="muted">Loading…</div>
          <table v-else class="as-table">
            <thead>
              <tr>
                <th>Dancer</th>
                <th>Service</th>
                <th>Options</th>
                <th class="num">Base</th>
                <th class="num">Gift credit</th>
                <th class="num">Net</th>
                <th>Status</th>
                <th>Notes</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              <template v-for="group in groupedVisibleRows">
                <tr class="dancer-header" :key="'h-' + group.groupKey">
                  <td colspan="6">
                    <strong>{{ group.dancerName }}</strong>
                    <span class="muted small"> · {{ group.dancerEmail }}</span>
                    <span v-if="group.pendingSignups.length" class="muted small">
                      · {{ group.pendingSignups.length }} pending, <strong>${{ (group.pendingCents / 100).toFixed(2) }}</strong> to charge
                    </span>
                    <span v-else-if="group.signups.length" class="muted small">
                      · all charged
                    </span>
                  </td>
                  <td colspan="3">
                    <template v-if="group.pendingSignups.length || group.failedSignups.length || group.invoicedSignups.length">
                      <v-btn
                        v-if="group.pendingCents > 0"
                        small color="primary"
                        :loading="charging === group.groupKey"
                        @click="chargeDancer(group)"
                      >
                        Charge ${{ (group.pendingCents / 100).toFixed(2) }}
                      </v-btn>
                      <v-btn
                        small outlined
                        :loading="markingPaid === group.groupKey"
                        @click="markDancerPaid(group)"
                      >
                        ✓ Mark {{ group.pendingSignups.length + group.failedSignups.length + group.invoicedSignups.length }} paid
                      </v-btn>
                      <v-btn
                        v-if="group.failedSignups.length && stripeLinkFor(group)"
                        small outlined color="error"
                        :href="stripeLinkFor(group)"
                        target="_blank" rel="noopener"
                      >
                        → Stripe customer
                      </v-btn>
                      <v-btn
                        v-if="group.pendingCents > 0 || group.failedSignups.length"
                        small outlined
                        :loading="invoicing === group.groupKey"
                        @click="sendInvoiceForDancer(group)"
                      >
                        📧 Send invoice
                      </v-btn>
                    </template>
                    <v-btn
                      v-if="group.hostedInvoiceUrl"
                      small outlined color="info"
                      :href="group.hostedInvoiceUrl"
                      target="_blank" rel="noopener"
                    >
                      → Invoice
                    </v-btn>
                  </td>
                </tr>
                <tr v-for="r in group.signups" :key="r.id"
                  class="dancer-sub"
                  :class="{ 'row-paid': r.status === 'paid', 'row-failed': r.status === 'failed', 'row-5photo': r.giftExtraPhotos > 0 }"
                >
                  <td></td>
                  <td>{{ r.serviceLabel }}</td>
                  <td class="opts">
                    <span v-if="r.giftExtraPhotos > 0" class="badge-5photo">🎁 5 photos included</span>
                    {{ r.optionsLabel }}
                  </td>
                  <td class="num">${{ ((r.basePriceCents || 0) / 100).toFixed(2) }}</td>
                  <td class="num pos">
                    {{ r.giftCreditCents > 0 ? '−$' + (r.giftCreditCents / 100).toFixed(2) : '—' }}
                  </td>
                  <td class="num"><strong>${{ ((r.netCents || 0) / 100).toFixed(2) }}</strong></td>
                  <td>
                    <span class="status" :class="'status-' + r.status">{{ r.status }}</span>
                  </td>
                  <td class="notes muted small">{{ r.notes || '' }}</td>
                  <td>
                    <v-btn
                      v-if="r.status !== 'paid'"
                      x-small text
                      :loading="markingPaid === r.id"
                      @click="markRowPaid(r)"
                    >✓ mark paid</v-btn>
                    <v-btn x-small text color="error" @click="confirmDelete(r)">×</v-btn>
                  </td>
                </tr>
              </template>
              <tr v-if="!visibleRows.length">
                <td colspan="9" class="muted small">No signups yet — click "+ Add signup" or "Paste CSV".</td>
              </tr>
            </tbody>
          </table>
        </v-container>
      </v-card-text>
    </v-flex></v-layout>

    <!-- Add signup dialog -->
    <v-dialog v-model="showAdd" max-width="560" scrollable>
      <v-card>
        <v-card-title>Add signup</v-card-title>
        <v-card-text>
          <div class="as-field">
            <label>Dancer</label>
            <select v-model="addForm.dancerId" class="as-input" @change="onAddDancerChange">
              <option value="">— pick a dancer —</option>
              <option v-for="d in dancersSorted" :key="d.id" :value="d.id">
                {{ d.lastName }}, {{ d.firstName }} · {{ d.email }}
              </option>
            </select>
          </div>
          <div class="as-field">
            <label>Service</label>
            <select v-model="addForm.serviceType" class="as-input" @change="onServiceChange">
              <option value="">— pick a service —</option>
              <option value="photoshoot">Photoshoot</option>
              <option value="pt">Physical Therapy (Dr. Nina Geromel)</option>
              <option value="privateLesson">Private Lesson</option>
              <option value="carpool">Airport Carpool</option>
            </select>
          </div>
          <template v-if="addForm.serviceType === 'carpool'">
            <div class="as-field">
              <label>Route</label>
              <select v-model="addForm.carpoolVariant" class="as-input">
                <option v-for="(v, k) in carpoolVariants" :key="k" :value="k">
                  {{ v.label }} — ${{ (v.priceCents / 100).toFixed(2) }}
                  <span v-if="v.giftEligible"> · gift-eligible</span>
                </option>
              </select>
            </div>
          </template>
          <template v-if="addForm.serviceType === 'photoshoot'">
            <div class="as-field">
              <label>Package</label>
              <select v-model="addForm.variant" class="as-input">
                <option value="indoor-1">1 slot + 2 edited photos — Indoor ($85)</option>
                <option value="outdoor-1">1 slot + 2 edited photos — Outdoor ($100)</option>
                <option value="indoor-2">2 slots + 5 edited photos — Indoor ($200)</option>
              </select>
            </div>
            <div class="as-field-row">
              <label class="as-inline">
                Extra edited photos ($30 each)
                <input type="number" min="0" max="20" v-model.number="addForm.extraPhotos" class="as-input-sm" />
              </label>
              <label class="as-inline">
                <input type="checkbox" v-model="addForm.rawProofs" />
                All raw proofs ($200)
              </label>
            </div>
            <div class="as-field-row">
              <label class="as-inline as-addon-only">
                <input type="checkbox" v-model="addForm.addOnsOnly" />
                Add-ons only — already paid for the base shoot (don't charge the package price)
              </label>
            </div>
          </template>
          <template v-if="addForm.serviceType === 'privateLesson'">
            <div class="as-field">
              <label>Teacher</label>
              <input v-model="addForm.teacher" class="as-input" placeholder="e.g. Sean Kelly" />
            </div>
          </template>
          <div class="as-field">
            <label>Notes</label>
            <input v-model="addForm.notes" class="as-input" placeholder="Optional" />
          </div>
          <div v-if="addForm.serviceType" class="as-summary-mini">
            <div v-if="addForm.serviceType === 'photoshoot' && addForm.addOnsOnly" class="muted small">
              Base shoot already paid — charging add-ons only.
            </div>
            <div>{{ (addForm.serviceType === 'photoshoot' && addForm.addOnsOnly) ? 'Add-ons' : 'Base price' }}: ${{ (addPreview.basePriceCents / 100).toFixed(2) }}</div>
            <div v-if="addPreview.giftCreditCents > 0" class="pos">
              Welcome-gift credit: −${{ (addPreview.giftCreditCents / 100).toFixed(2) }}
              <span class="muted small">({{ addPreview.giftsRemaining }} of {{ addPreview.giftsTotal }} left for this dancer)</span>
            </div>
            <div><strong>Net: ${{ (addPreview.netCents / 100).toFixed(2) }}</strong></div>
          </div>
          <p v-if="addError" class="error--text small">{{ addError }}</p>
        </v-card-text>
        <v-card-actions>
          <v-btn text @click="showAdd = false">Cancel</v-btn>
          <v-spacer />
          <v-btn color="primary" :loading="adding" :disabled="!canAdd" @click="submitAdd">Save signup</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Import from carpools dialog -->
    <v-dialog v-model="showCarpoolImport" max-width="880" scrollable>
      <v-card>
        <v-card-title>Import carpool signups</v-card-title>
        <v-card-text>
          <p class="muted small">
            Scans <code>iabf-carpools-2026</code> for every confirmed rider — skips waitlist rows, guest riders,
            and any dancer/route that's already in the table below. Same welcome-gift rule applies (MCO/DAB trips
            eligible: 1 gift = 1 MCO way or full DAB; 2 gifts = full MCO). Uncheck a row if you don't want it.
          </p>
          <p v-if="carpoolImportLoading" class="muted">Scanning slots…</p>
          <p v-else-if="!carpoolImportRows.length && !carpoolImportError" class="muted">
            Nothing new to import — every confirmed rider is already in the table.
          </p>
          <p v-if="carpoolImportError" class="error--text small">{{ carpoolImportError }}</p>

          <table v-if="carpoolImportRows.length" class="as-table sm">
            <thead>
              <tr>
                <th>
                  <input type="checkbox"
                    :checked="carpoolImportRows.length > 0 && carpoolImportRows.every(r => r.selected)"
                    @change="toggleAllCarpoolImport($event.target.checked)" />
                </th>
                <th>Dancer</th>
                <th>Route</th>
                <th>Slot(s)</th>
                <th class="num">Price</th>
                <th class="num">Gift</th>
                <th class="num">Net</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in carpoolImportRows" :key="row.key">
                <td><input type="checkbox" v-model="row.selected" /></td>
                <td>
                  <div>{{ row.dancerName }}</div>
                  <div class="muted small">{{ row.dancerEmail }}</div>
                </td>
                <td>{{ row.variantLabel }}</td>
                <td class="muted small">{{ row.note }}</td>
                <td class="num">${{ (row.basePriceCents / 100).toFixed(2) }}</td>
                <td class="num pos">
                  {{ row.giftCreditCents > 0 ? '−$' + (row.giftCreditCents / 100).toFixed(2) : '—' }}
                </td>
                <td class="num"><strong>${{ (row.netCents / 100).toFixed(2) }}</strong></td>
              </tr>
            </tbody>
          </table>
        </v-card-text>
        <v-card-actions>
          <v-btn text @click="showCarpoolImport = false">Cancel</v-btn>
          <v-spacer />
          <v-btn color="primary" :loading="carpoolImporting" :disabled="!carpoolImportRows.some(r => r.selected)" @click="submitCarpoolImport">
            Import {{ carpoolImportRows.filter(r => r.selected).length }} row(s)
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Paste-names dialog (one Google Sheet per service) -->
    <v-dialog v-model="showCsv" max-width="820" scrollable>
      <v-card>
        <v-card-title>Paste signups from Google Sheet</v-card-title>
        <v-card-text>
          <p class="muted small">
            Pick the service + options that apply to <em>every</em> row, then paste one name per line
            ("Phyllis M", "Anne WB", "Sakia Bryson"…). We loose-match against your dancer list —
            first name + last-name or last-initials, so "Anne WB" resolves to "Anne Witteman Borowitz".
          </p>

          <div class="as-field">
            <label>Service</label>
            <select v-model="csvService" class="as-input" @change="csvRows = []">
              <option value="">— pick a service —</option>
              <option value="photoshoot">Photoshoot</option>
              <option value="pt">Physical Therapy (Dr. Nina Geromel)</option>
              <option value="privateLesson">Private Lesson</option>
              <option value="carpool">Airport Carpool</option>
            </select>
          </div>
          <template v-if="csvService === 'carpool'">
            <div class="as-field">
              <label>Route (applies to every row)</label>
              <select v-model="csvCarpoolVariant" class="as-input" @change="csvRows = []">
                <option v-for="(v, k) in carpoolVariants" :key="k" :value="k">
                  {{ v.label }} — ${{ (v.priceCents / 100).toFixed(2) }}
                  <span v-if="v.giftEligible"> · gift-eligible</span>
                </option>
              </select>
            </div>
            <p class="muted small">
              Gift "1-Way Airport Carpool (MCO) or 2-way Daytona (DAB)": one selection covers one MCO leg
              or a full DAB roundtrip. Selecting the carpool gift twice covers a full MCO roundtrip.
              Legacy "Roundtrip Airport Carpool" counts as two credits.
            </p>
          </template>
          <template v-if="csvService === 'photoshoot'">
            <div class="as-field">
              <label>Package (applies to every row)</label>
              <select v-model="csvVariant" class="as-input" @change="csvRows = []">
                <option value="indoor-1">1 slot + 2 edited photos — Indoor ($85)</option>
                <option value="outdoor-1">1 slot + 2 edited photos — Outdoor ($100)</option>
                <option value="indoor-2">2 slots + 5 edited photos — Indoor ($200)</option>
              </select>
            </div>
            <div class="as-field-row">
              <label class="as-inline">
                Extra edited photos ($30 each, applied to every row)
                <input type="number" min="0" max="20" v-model.number="csvExtraPhotos" class="as-input-sm" @input="csvRows = []" />
              </label>
              <label class="as-inline">
                <input type="checkbox" v-model="csvRawProofs" @change="csvRows = []" />
                All raw proofs ($200)
              </label>
            </div>
            <p class="muted small">
              If rows in your sheet have different variants, paste them in separate batches by variant.
            </p>
          </template>

          <div class="as-field">
            <label>
              {{ csvService === 'privateLesson' ? 'Teacher & Student — one per line' : 'Names (one per line)' }}
            </label>
            <p v-if="csvService === 'privateLesson'" class="muted small">
              Separate teacher and student with a pipe <code>|</code>, a tab, or 2+ spaces —
              whatever you copied out of your sheet.
            </p>
            <textarea
              v-if="csvService === 'privateLesson'"
              v-model="csvText" class="as-csv" rows="10"
              placeholder="Sean Kelly    Sakia Bryson&#10;Jena Kocsis    Anne WB&#10;Cindy Heen    Phyllis M"
            ></textarea>
            <textarea
              v-else
              v-model="csvText" class="as-csv" rows="10"
              placeholder="Phyllis M&#10;Jessica M&#10;Gabrielle E&#10;Sakia Bryson&#10;Anne WB"
            ></textarea>
          </div>

          <v-btn small outlined class="mt-2" :disabled="!csvService" @click="parseCsvPreview">
            Preview {{ csvRows.length ? '(re-parse)' : '' }}
          </v-btn>

          <div v-if="csvRows.length" class="as-csv-preview">
            <h4>Preview — {{ csvRows.length }} row(s), {{ csvValidCount }} valid</h4>
            <table class="as-table sm">
              <thead>
                <tr>
                  <th>#</th><th>Pasted</th>
                  <th v-if="csvService === 'privateLesson'">Teacher</th>
                  <th>Matched dancer</th>
                  <th class="num">Base</th><th class="num">Gift</th><th class="num">Net</th><th>Status</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(row, i) in csvRows" :key="i" :class="{ 'row-invalid': !row.valid, 'row-5photo': row.giftExtraPhotos > 0 }">
                  <td>{{ i + 1 }}</td>
                  <td>{{ row.raw }}</td>
                  <td v-if="csvService === 'privateLesson'">{{ row.teacher || '—' }}</td>
                  <td>
                    {{ row.dancerName || '—' }}
                    <span v-if="row.giftExtraPhotos > 0" class="badge-5photo">🎁 5 photos</span>
                  </td>
                  <td class="num">${{ ((row.basePriceCents || 0) / 100).toFixed(2) }}</td>
                  <td class="num pos">{{ row.giftCreditCents > 0 ? '−$' + (row.giftCreditCents / 100).toFixed(2) : '—' }}</td>
                  <td class="num">${{ ((row.netCents || 0) / 100).toFixed(2) }}</td>
                  <td>
                    <span v-if="row.valid" class="status status-pending">ready</span>
                    <span v-else class="status status-failed" :title="row.error">skip · {{ row.error }}</span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <p v-if="csvError" class="error--text small">{{ csvError }}</p>
        </v-card-text>
        <v-card-actions>
          <v-btn text @click="showCsv = false">Cancel</v-btn>
          <v-spacer />
          <v-btn color="primary" :loading="csvImporting" :disabled="!csvValidCount" @click="submitCsv">
            Import {{ csvValidCount }} signup{{ csvValidCount === 1 ? '' : 's' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import { db, functions } from '@/firebase'
import iabfAdminMixin from '@/utils/iabf-admin-mixin'
import {
  carpoolGiftCreditsFromDancer,
  carpoolAddonGiftCredit
} from '@/config/iabf-gifts'


const GIFT_LABELS = {
  // 2027 labels + legacy 2026 labels for credit matching
  standard: [
    'Free Indoor or Outdoor photoshoot with 2 edited photos',
    '15-min Photoshoot with 2 edited photos'
  ],
  fivePhoto: [
    '3 extra edited photos for your photoshoot',
    '15-min Photoshoot with 5 edited photos'
  ],
  carpool: [
    '1-Way Airport Carpool (MCO) or 2-way Daytona (DAB)',
    'Roundtrip Airport Carpool (Orlando and Daytona)'
  ]
}
const GIFT_CREDIT_CENTS = 8500 // $85 for either photoshoot gift version
const FIVE_PHOTO_BONUS_EXTRA = 3 // extra edited photos included with the 5-photo gift

// Carpool: per-direction prices (MCO=$60, DAB=$25). SFB is no longer offered.
// Gift: 1 selection = 1 free MCO leg OR full DAB roundtrip; 2 selections = full MCO free.
// Legacy "Roundtrip Airport Carpool" counts as 2 credits.
const CARPOOL_VARIANTS = {
  'mco-roundtrip': { label: 'Orlando (MCO) — roundtrip', priceCents: 12000, giftEligible: true },
  'mco-arrival': { label: 'Orlando (MCO) — arrival only', priceCents: 6000, giftEligible: true },
  'mco-departure': { label: 'Orlando (MCO) — departure only', priceCents: 6000, giftEligible: true },
  'dab-roundtrip': { label: 'Daytona (DAB) — roundtrip', priceCents: 5000, giftEligible: true },
  'dab-arrival': { label: 'Daytona (DAB) — arrival only', priceCents: 2500, giftEligible: true },
  'dab-departure': { label: 'Daytona (DAB) — departure only', priceCents: 2500, giftEligible: true }
}

const PHOTOSHOOT_VARIANTS = {
  'indoor-1': { label: '1 slot + 2 edited photos — Indoor', basePriceCents: 8500, basePhotos: 2, slots: 1, location: 'indoor' },
  'outdoor-1': { label: '1 slot + 2 edited photos — Outdoor', basePriceCents: 10000, basePhotos: 2, slots: 1, location: 'outdoor' },
  'indoor-2': { label: '2 slots + 5 edited photos — Indoor', basePriceCents: 20000, basePhotos: 5, slots: 2, location: 'indoor' }
}
const EXTRA_PHOTO_CENTS = 3000
const RAW_PROOFS_CENTS = 20000

const SERVICE_INFO = {
  photoshoot: { label: 'Photoshoot' },
  pt: { label: 'Physical Therapy (Dr. Nina Geromel)', basePriceCents: 5000 },
  privateLesson: { label: 'Private Lesson', basePriceCents: 5000 },
  carpool: { label: 'Airport Carpool' }
}

function matchesGift (value, labels) {
  const list = Array.isArray(labels) ? labels : [labels]
  return list.includes(value)
}

function countGifts (dancer) {
  // Returns { standard, fivePhoto, carpool, total } for a single dancer.
  // carpool is gift CREDITS: 1-way gift = 1, legacy roundtrip gift = 2.
  const gifts = [
    dancer.registrationGift1,
    dancer.registrationGift2,
    dancer.registrationGift3,
    dancer.registrationGift4,
    dancer.earlyBirdGift
  ].map(g => String(g || '').trim())
  const standard = gifts.filter(g => matchesGift(g, GIFT_LABELS.standard)).length
  const fivePhoto = gifts.filter(g => matchesGift(g, GIFT_LABELS.fivePhoto)).length
  const carpool = carpoolGiftCreditsFromDancer(dancer)
  return { standard, fivePhoto, carpool, total: standard + fivePhoto + carpool }
}

/** How many carpool gift credits a prior signup row already consumed. */
function carpoolCreditsUsedByRow (row) {
  if (!row || !row.giftCreditCents) {
    return 0
  }
  const variant = String(row.variant || '')
  const base = Number(row.basePriceCents) || 0
  const credit = Number(row.giftCreditCents) || 0
  if (variant === 'mco-roundtrip') {
    // Full free = 2 credits; half (one way) = 1 credit.
    return credit >= base && base > 0 ? 2 : 1
  }
  return 1
}
// Prefer to consume 5-photo gifts first (bigger value) so the bonus
// extras are used up rather than left on the table.
function pickGift (giftCounts, consumed) {
  const usedFive = consumed.fivePhoto || 0
  const usedStd = consumed.standard || 0
  const remFive = Math.max(0, giftCounts.fivePhoto - usedFive)
  const remStd = Math.max(0, giftCounts.standard - usedStd)
  if (remFive > 0) return { type: 'fivePhoto', extraPhotos: FIVE_PHOTO_BONUS_EXTRA }
  if (remStd > 0) return { type: 'standard', extraPhotos: 0 }
  return { type: '', extraPhotos: 0 }
}

export default {
  mixins: [iabfAdminMixin],
  data () {
    return {
      loading: true,
      rows: [],
      unsub: null,
      dancers: [],
      charging: null,
      markingPaid: null,
      invoicing: null,
      bulkCharging: false,
      bulkProgress: '',
      filterService: '',
      filterStatus: '',
      // Add-signup dialog
      showAdd: false,
      adding: false,
      addError: '',
      addForm: this.emptyAddForm(),
      // Paste-names dialog (one dialog per Google Sheet, per service)
      payoutCopyFlash: {},
      showCsv: false,
      csvService: '',
      csvVariant: 'indoor-1',
      csvExtraPhotos: 0,
      csvRawProofs: false,
      csvCarpoolVariant: 'mco-roundtrip',
      csvText: '',
      csvRows: [],
      csvError: '',
      csvImporting: false,
      // Manual "what I spent" totals for the P&L panels — persisted
      // in iabf-config-2026/addon-services-pnl so they survive reloads.
      pnl: {
        photoshootSpendCents: 0,
        carpoolSpendCents: 0,
        loaded: false,
        saving: null,
        savedFlash: null
      },
      pnlSpendDraft: { photoshoot: '', carpool: '' },
      unsubPnl: null,
      // Carpool import dialog (scan iabf-carpools-2026 for confirmed rides)
      showCarpoolImport: false,
      carpoolImportRows: [],
      carpoolImportError: '',
      carpoolImportLoading: false,
      carpoolImporting: false
    }
  },
  computed: {
    carpoolVariants () { return CARPOOL_VARIANTS },
    dancersSorted () {
      return (this.dancers || []).slice().sort((a, b) => {
        const ln = (a.lastName || '').localeCompare(b.lastName || '')
        return ln !== 0 ? ln : (a.firstName || '').localeCompare(b.firstName || '')
      })
    },
    dancerById () {
      const map = {}
      for (const d of this.dancers) map[d.id] = d
      return map
    },
    dancerByEmail () {
      const map = {}
      for (const d of this.dancers) {
        const e = (d.email || '').toLowerCase().trim()
        if (e) map[e] = d
      }
      return map
    },
    visibleRows () {
      return this.rows.filter(r => {
        if (this.filterService && r.serviceType !== this.filterService) return false
        if (this.filterStatus && r.status !== this.filterStatus) return false
        return true
      })
    },
    groupedVisibleRows () {
      // Group visible rows by dancer so each dancer's services are
      // shown as one block with a single Charge button. Ordering:
      // groups with pending charges first, then all-paid groups —
      // matches how Julie works through the queue.
      const byKey = new Map()
      for (const r of this.visibleRows) {
        const key = r.dancerId || r.dancerEmail || r.dancerName
        if (!byKey.has(key)) {
          byKey.set(key, {
            groupKey: key,
            dancerId: r.dancerId,
            dancerName: r.dancerName,
            dancerEmail: r.dancerEmail,
            signups: []
          })
        }
        byKey.get(key).signups.push(r)
      }
      const groups = Array.from(byKey.values()).map(g => {
        const pending = g.signups.filter(s => s.status === 'pending')
        const failed = g.signups.filter(s => s.status === 'failed')
        const invoiced = g.signups.filter(s => s.status === 'invoiced')
        // If any signup has a hosted invoice URL, expose it so the
        // group header can show a quick "→ Invoice" button.
        const invoiceRow = g.signups.find(s => s.stripeInvoiceUrl)
        return {
          ...g,
          pendingSignups: pending,
          failedSignups: failed,
          invoicedSignups: invoiced,
          hostedInvoiceUrl: invoiceRow ? invoiceRow.stripeInvoiceUrl : '',
          pendingCents: pending.reduce((s, r) => s + (Number(r.netCents) || 0), 0),
        }
      })
      groups.sort((a, b) => {
        if (a.pendingCents !== b.pendingCents) return b.pendingCents - a.pendingCents
        return (a.dancerName || '').localeCompare(b.dancerName || '')
      })
      return groups
    },
    pendingRows () { return this.rows.filter(r => r.status === 'pending') },
    pendingDancerCount () {
      return this.groupedVisibleRows.filter(g => g.pendingSignups.length).length
    },
    paidRows () { return this.rows.filter(r => r.status === 'paid') },
    pendingTotalCents () {
      return this.pendingRows.reduce((s, r) => s + (Number(r.netCents) || 0), 0)
    },
    photoshootRoster () {
      // One entry per dancer with at least one photoshoot signup.
      // Slots stay broken by location (Yoshi shoots indoor, Spencer
      // shoots outdoor), but shot count is a single "totalShots" —
      // the dancer can weight her final edits toward whichever
      // location she likes best.
      const map = {}
      for (const r of this.rows) {
        if (r.serviceType !== 'photoshoot') continue
        const key = r.dancerId || r.dancerName
        if (!map[key]) {
          map[key] = {
            dancerName: r.dancerName || '(unknown)',
            dancerEmail: r.dancerEmail || '',
            indoorSlots: 0, outdoorSlots: 0,
            totalShots: 0,
            rawProofs: false
          }
        }
        const m = map[key]
        const v = PHOTOSHOOT_VARIANTS[r.variant]
        if (!v) continue
        // Add-ons-only rows are post-shoot upsells — the base shoot (its slot
        // + base photos) was already counted on the original signup, so only
        // the newly purchased extra edits count here, and no new slot is added.
        const baseShots = r.addOnsOnly ? 0 : (v.basePhotos || 0)
        const shots = baseShots + (r.extraPhotos || 0) + (r.giftExtraPhotos || 0)
        if (!r.addOnsOnly) {
          if (v.location === 'outdoor') m.outdoorSlots += v.slots
          else m.indoorSlots += v.slots
        }
        m.totalShots += shots
        if (r.rawProofs) m.rawProofs = true
      }
      return Object.values(map).sort((a, b) => a.dancerName.localeCompare(b.dancerName))
    },
    photoshootRosterTotals () {
      return this.photoshootRoster.reduce((acc, r) => ({
        dancers: acc.dancers + 1,
        indoorSlots: acc.indoorSlots + r.indoorSlots,
        outdoorSlots: acc.outdoorSlots + r.outdoorSlots,
        totalShots: acc.totalShots + r.totalShots
      }), { dancers: 0, indoorSlots: 0, outdoorSlots: 0, totalShots: 0 })
    },
    photoshootPnl () {
      return this.buildPnl('photoshoot', this.pnl.photoshootSpendCents)
    },
    carpoolPnl () {
      return this.buildPnl('carpool', this.pnl.carpoolSpendCents)
    },
    payouts () {
      // Photoshoots don't need payout (Yoshi & Spencer are covered).
      // Group Private Lessons by teacher and roll PT under Dr. Nina.
      const map = {}
      for (const r of this.rows) {
        let name = ''
        if (r.serviceType === 'privateLesson') {
          name = (r.teacher || '').trim() || '(no teacher recorded)'
        } else if (r.serviceType === 'pt') {
          name = 'Dr. Nina Geromel (PT)'
        } else {
          continue
        }
        if (!map[name]) map[name] = { name, sessions: 0, collectedCents: 0, uncollectedCents: 0 }
        map[name].sessions += 1
        if (r.status === 'paid') map[name].collectedCents += Number(r.netCents) || 0
        else map[name].uncollectedCents += Number(r.netCents) || 0
      }
      return Object.values(map).sort((a, b) => a.name.localeCompare(b.name))
    },
    payoutsTotals () {
      return this.payouts.reduce((acc, p) => ({
        sessions: acc.sessions + p.sessions,
        collectedCents: acc.collectedCents + p.collectedCents,
        uncollectedCents: acc.uncollectedCents + p.uncollectedCents
      }), { sessions: 0, collectedCents: 0, uncollectedCents: 0 })
    },
    addPreview () {
      const f = this.addForm
      const dancer = this.dancerById[f.dancerId] || null
      let basePriceCents = 0
      let carpoolVariant = null
      if (f.serviceType === 'photoshoot') {
        // Add-ons only: the dancer already paid for the base shoot at their
        // original signup and is now buying extras off their proofs, so the
        // package base price is excluded — charge only the add-ons.
        const v = PHOTOSHOOT_VARIANTS[f.variant]
        if (v && !f.addOnsOnly) basePriceCents = v.basePriceCents
        basePriceCents += Math.max(0, Number(f.extraPhotos) || 0) * EXTRA_PHOTO_CENTS
        if (f.rawProofs) basePriceCents += RAW_PROOFS_CENTS
      } else if (f.serviceType === 'carpool') {
        carpoolVariant = CARPOOL_VARIANTS[f.carpoolVariant]
        if (carpoolVariant) basePriceCents = carpoolVariant.priceCents
      } else if (SERVICE_INFO[f.serviceType]) {
        basePriceCents = SERVICE_INFO[f.serviceType].basePriceCents || 0
      }
      const giftCounts = dancer ? countGifts(dancer) : { standard: 0, fivePhoto: 0, carpool: 0, total: 0 }
      // Tally credits already consumed by this dancer's OTHER signups.
      const consumed = { standard: 0, fivePhoto: 0, carpool: 0 }
      for (const r of this.rows) {
        if (r.dancerId !== f.dancerId || !r.giftCreditCents) continue
        if (r.serviceType === 'photoshoot') {
          if (r.giftExtraPhotos > 0) consumed.fivePhoto += 1
          else consumed.standard += 1
        } else if (r.serviceType === 'carpool') {
          consumed.carpool += carpoolCreditsUsedByRow(r)
        }
      }
      let giftCreditCents = 0
      let giftExtraPhotos = 0
      let pickedType = ''
      if (f.serviceType === 'photoshoot' && !f.addOnsOnly) {
        // The welcome-gift credit applies to a base shoot, not to a pure
        // add-on purchase — skip it entirely when charging add-ons only.
        const pick = pickGift(giftCounts, consumed)
        pickedType = pick.type
        giftExtraPhotos = pick.extraPhotos
        if (pick.type) giftCreditCents = Math.min(GIFT_CREDIT_CENTS, basePriceCents)
      } else if (f.serviceType === 'carpool' && carpoolVariant && carpoolVariant.giftEligible) {
        const remainingCarpool = giftCounts.carpool - consumed.carpool
        const applied = carpoolAddonGiftCredit(f.carpoolVariant, basePriceCents, remainingCarpool)
        giftCreditCents = applied.giftCreditCents
        if (applied.creditsUsed > 0) {
          pickedType = 'carpool'
        }
      }
      const netCents = Math.max(0, basePriceCents - giftCreditCents)
      const giftsRemaining = Math.max(0, giftCounts.total - (consumed.standard + consumed.fivePhoto + consumed.carpool))
      return {
        basePriceCents, giftCreditCents, giftExtraPhotos, netCents,
        giftsTotal: giftCounts.total, giftsRemaining, pickedType
      }
    },
    canAdd () {
      const f = this.addForm
      if (!f.dancerId || !f.serviceType) return false
      if (f.serviceType === 'photoshoot' && !f.variant) return false
      if (f.serviceType === 'carpool' && !f.carpoolVariant) return false
      return this.addPreview.basePriceCents > 0
    },
    csvValidCount () {
      return this.csvRows.filter(r => r.valid).length
    }
  },
  async created () {
    this.$store.commit('setLayout', 'iabf-layout')
    // Load dancer list (we need email + welcome gifts for matching + credit).
    const snap = await db.collection(this.IABF.dancers).get()
    this.dancers = snap.docs.map(d => ({ id: d.id, ...d.data() }))
    // Live-subscribe to the manual P&L spend totals so the two
    // "you spent" fields stay in sync across sessions.
    this.unsubPnl = db.collection(this.IABF.config).doc('addon-services-pnl').onSnapshot(doc => {
      const data = doc.exists ? (doc.data() || {}) : {}
      this.pnl.photoshootSpendCents = Number(data.photoshootSpendCents) || 0
      this.pnl.carpoolSpendCents = Number(data.carpoolSpendCents) || 0
      this.pnl.loaded = true
      // Seed the input drafts only on first load so live typing isn't clobbered.
      if (!this.pnlSpendDraft.photoshoot) this.$set(this.pnlSpendDraft, 'photoshoot', this.pnlSpendDollarsFor('photoshoot'))
      if (!this.pnlSpendDraft.carpool) this.$set(this.pnlSpendDraft, 'carpool', this.pnlSpendDollarsFor('carpool'))
    })
    this.unsub = db.collection(this.IABF.addonServices).onSnapshot(s => {
      this.rows = s.docs.map(d => ({ id: d.id, ...d.data() })).sort((a, b) => {
        const ta = a.createdAt && a.createdAt.toMillis ? a.createdAt.toMillis() : 0
        const tb = b.createdAt && b.createdAt.toMillis ? b.createdAt.toMillis() : 0
        return tb - ta
      })
      this.loading = false
    })
  },
  beforeDestroy () {
    if (typeof this.unsub === 'function') this.unsub()
    if (typeof this.unsubPnl === 'function') this.unsubPnl()
  },
  methods: {
    emptyAddForm () {
      return { dancerId: '', serviceType: '', variant: 'indoor-1', extraPhotos: 0, rawProofs: false, addOnsOnly: false, teacher: '', carpoolVariant: 'mco-roundtrip', notes: '' }
    },
    openAdd () {
      this.addForm = this.emptyAddForm()
      this.addError = ''
      this.showAdd = true
    },
    onAddDancerChange () {
      // (Preview auto-recomputes; nothing else to do.)
    },
    onServiceChange () {
      if (this.addForm.serviceType !== 'photoshoot') {
        this.addForm.variant = 'indoor-1'
        this.addForm.extraPhotos = 0
        this.addForm.rawProofs = false
        this.addForm.addOnsOnly = false
      }
    },
    optionsLabel (r) {
      if (r.serviceType === 'photoshoot') {
        const bits = []
        const v = PHOTOSHOOT_VARIANTS[r.variant]
        if (r.addOnsOnly) bits.push('Add-ons only')
        else if (v) bits.push(v.label)
        if (r.extraPhotos > 0) bits.push(`+${r.extraPhotos} extra photo${r.extraPhotos === 1 ? '' : 's'}`)
        if (r.rawProofs) bits.push('+ all raw proofs')
        return bits.join(' · ')
      }
      if (r.serviceType === 'privateLesson' && r.teacher) {
        return 'w/ ' + r.teacher
      }
      if (r.serviceType === 'carpool') {
        const v = CARPOOL_VARIANTS[r.variant]
        return v ? v.label : ''
      }
      return ''
    },
    async submitAdd () {
      if (!this.canAdd) return
      const dancer = this.dancerById[this.addForm.dancerId]
      if (!dancer) return
      this.adding = true
      this.addError = ''
      try {
        const p = this.addPreview
        const payload = {
          dancerId: dancer.id,
          dancerName: `${dancer.firstName || ''} ${dancer.lastName || ''}`.trim(),
          dancerEmail: (dancer.email || '').toLowerCase(),
          serviceType: this.addForm.serviceType,
          serviceLabel: SERVICE_INFO[this.addForm.serviceType].label,
          teacher: this.addForm.serviceType === 'privateLesson' ? (this.addForm.teacher || '').trim() : '',
          variant: this.addForm.serviceType === 'photoshoot'
            ? this.addForm.variant
            : (this.addForm.serviceType === 'carpool' ? this.addForm.carpoolVariant : ''),
          extraPhotos: this.addForm.serviceType === 'photoshoot' ? Math.max(0, Number(this.addForm.extraPhotos) || 0) : 0,
          rawProofs: this.addForm.serviceType === 'photoshoot' ? !!this.addForm.rawProofs : false,
          addOnsOnly: this.addForm.serviceType === 'photoshoot' ? !!this.addForm.addOnsOnly : false,
          basePriceCents: p.basePriceCents,
          giftCreditCents: p.giftCreditCents,
          giftExtraPhotos: p.giftExtraPhotos || 0,
          netCents: p.netCents,
          status: 'pending',
          notes: this.addForm.notes || '',
          createdAt: new Date()
        }
        payload.optionsLabel = this.optionsLabel(payload)
        await db.collection(this.IABF.addonServices).add(payload)
        this.showAdd = false
      } catch (e) {
        console.error('Add failed:', e)
        this.addError = e.message || 'Failed to save.'
      }
      this.adding = false
    },
    stripeLinkFor (group) {
      // Pull the dancer's stripeLink field for opening the Stripe
      // customer dashboard when a charge failed. Fall back gracefully
      // if the dancer or the field isn't present.
      const dancer = group && group.dancerId ? this.dancerById[group.dancerId] : null
      if (!dancer) return ''
      const link = String(dancer.stripeLink || '').trim()
      return /^https?:\/\//.test(link) ? link : ''
    },
    async markRowPaid (row) {
      // Manual override — flips a single signup to paid without Stripe.
      // For cases where the dancer paid out-of-band (Venmo, cash, or a
      // gift-covered $0 row you don't want to run through Charge).
      const dollars = ((row.netCents || 0) / 100).toFixed(2)
      // eslint-disable-next-line no-alert
      if (!window.confirm(`Mark this ${row.serviceLabel} row paid ($${dollars})?\n\nNo Stripe charge will run — use only if the money was collected another way, or the total is $0 after gifts.`)) return
      this.markingPaid = row.id
      try {
        await db.collection(this.IABF.addonServices).doc(row.id).update({
          status: 'paid',
          paidAt: new Date(),
          stripeChargeSkipped: true,
          manuallyMarkedPaid: true,
          lastError: null
        })
      } catch (e) {
        console.error('Mark paid failed:', e)
        // eslint-disable-next-line no-alert
        window.alert('Mark paid failed: ' + (e.message || e))
      }
      this.markingPaid = null
    },
    async markDancerPaid (group) {
      const targets = [
        ...(group.pendingSignups || []),
        ...(group.failedSignups || []),
        ...(group.invoicedSignups || []),
      ]
      if (!targets.length) return
      const totalCents = targets.reduce((s, r) => s + (Number(r.netCents) || 0), 0)
      // eslint-disable-next-line no-alert
      if (!window.confirm(`Mark all ${targets.length} row(s) for ${group.dancerName} paid ($${(totalCents / 100).toFixed(2)})?\n\nNo Stripe charge will run — use only if the money was collected another way, or the total is $0 after gifts.`)) return
      this.markingPaid = group.groupKey
      const now = new Date()
      try {
        // Small parallel batch (usually < 5 rows per dancer).
        await Promise.all(targets.map(r =>
          db.collection(this.IABF.addonServices).doc(r.id).update({
            status: 'paid',
            paidAt: now,
            stripeChargeSkipped: true,
            manuallyMarkedPaid: true,
            lastError: null
          })
        ))
      } catch (e) {
        console.error('Mark dancer paid failed:', e)
        // eslint-disable-next-line no-alert
        window.alert('Mark paid failed: ' + (e.message || e))
      }
      this.markingPaid = null
    },
    async sendInvoiceForDancer (group) {
      // Alternative to charging a saved card: Stripe finalizes and
      // emails an invoice the dancer pays herself. Any signup that's
      // pending OR failed gets bundled into one invoice.
      const targets = [...(group.pendingSignups || []), ...(group.failedSignups || [])]
        .filter(s => (Number(s.netCents) || 0) > 0)
      if (!targets.length) return
      const total = targets.reduce((s, r) => s + (Number(r.netCents) || 0), 0)
      const summary = targets.map(s => `· ${s.serviceLabel}${s.optionsLabel ? ' — ' + s.optionsLabel : ''}: $${((s.netCents || 0) / 100).toFixed(2)}`).join('\n')
      // eslint-disable-next-line no-alert
      if (!window.confirm(`Send Stripe invoice to ${group.dancerName} for $${(total / 100).toFixed(2)}?\n\n${summary}\n\nStripe emails a hosted invoice link; the dancer pays it herself (due in 1 day).`)) return
      this.invoicing = group.groupKey
      try {
        const callable = functions.httpsCallable('sendInvoiceForAddonSignups')
        const res = await callable({
          signupIds: targets.map(s => s.id),
          festivalYear: this.IABF.year
        })
        if (res && res.data && res.data.hostedInvoiceUrl) {
          // eslint-disable-next-line no-alert
          if (window.confirm(`Invoice sent. Open the hosted invoice page now?`)) {
            window.open(res.data.hostedInvoiceUrl, '_blank', 'noopener')
          }
        }
      } catch (e) {
        console.error('Invoice failed:', e)
        // eslint-disable-next-line no-alert
        window.alert('Invoice failed: ' + (e.message || e))
      }
      this.invoicing = null
    },
    async chargeDancer (group) {
      const pending = group.pendingSignups || []
      if (!pending.length) return
      const summary = pending.map(s => `· ${s.serviceLabel}${s.optionsLabel ? ' — ' + s.optionsLabel : ''}: $${((s.netCents || 0) / 100).toFixed(2)}`).join('\n')
      // eslint-disable-next-line no-alert
      if (!window.confirm(`Charge ${group.dancerName} $${(group.pendingCents / 100).toFixed(2)} for ${pending.length} service(s)?\n\n${summary}\n\nOne Stripe charge, description will list each item.`)) return
      this.charging = group.groupKey
      try {
        const callable = functions.httpsCallable('chargeAddonService')
        await callable({
          signupIds: pending.map(s => s.id),
          festivalYear: this.IABF.year
        })
      } catch (e) {
        console.error('Charge failed:', e)
        // eslint-disable-next-line no-alert
        window.alert('Charge failed: ' + (e.message || e))
        // Mark every attempted signup as failed so Julie can see what's stuck.
        for (const s of pending) {
          try {
            await db.collection(this.IABF.addonServices).doc(s.id).update({
              status: 'failed',
              lastError: (e && e.message) || String(e)
            })
          } catch (writeErr) {
            console.error('Failed to mark row failed:', writeErr)
          }
        }
      }
      this.charging = null
    },
    async chargeAllPending () {
      // Iterate GROUPS (one dancer at a time) so each dancer sees one
      // Stripe transaction. Bulk means "run through every dancer's
      // rolled-up charge", not one Stripe transaction per row.
      const groups = this.groupedVisibleRows.filter(g => g.pendingSignups.length)
      if (!groups.length) return
      // eslint-disable-next-line no-alert
      if (!window.confirm(`Charge ${groups.length} dancer(s), ${this.pendingRows.length} total service(s), $${(this.pendingTotalCents / 100).toFixed(2)}? Each dancer sees one Stripe charge with all their services in the description.`)) return
      this.bulkCharging = true
      let done = 0
      let failed = 0
      const callable = functions.httpsCallable('chargeAddonService')
      for (const group of groups) {
        this.bulkProgress = `${done + 1} / ${groups.length}: ${group.dancerName}…`
        try {
          await callable({
            signupIds: group.pendingSignups.map(s => s.id),
            festivalYear: this.IABF.year
          })
        } catch (e) {
          console.error('Bulk charge failed for', group.dancerName, e)
          failed += 1
          for (const s of group.pendingSignups) {
            try {
              await db.collection(this.IABF.addonServices).doc(s.id).update({
                status: 'failed',
                lastError: (e && e.message) || String(e)
              })
            } catch (writeErr) {
              console.error('Failed to mark row failed:', writeErr)
            }
          }
        }
        done += 1
      }
      this.bulkCharging = false
      this.bulkProgress = ''
      // eslint-disable-next-line no-alert
      window.alert(`Done. ${done - failed} dancer(s) charged, ${failed} failed. Failed rows are marked in the table.`)
    },
    formatRosterLine (r) {
      const slotBits = []
      if (r.indoorSlots > 0) slotBits.push(`${r.indoorSlots} indoor`)
      if (r.outdoorSlots > 0) slotBits.push(`${r.outdoorSlots} outdoor`)
      const raw = r.rawProofs ? ' + raw proofs' : ''
      return `${r.dancerName} — ${r.totalShots} total shot${r.totalShots === 1 ? '' : 's'} across ${slotBits.join(' + ')} slot${(r.indoorSlots + r.outdoorSlots) === 1 ? '' : 's'}${raw}`
    },
    async copyPhotoshootRoster () {
      const lines = this.photoshootRoster.map(r => this.formatRosterLine(r))
      const text = lines.join('\n')
      try {
        await navigator.clipboard.writeText(text)
        this.$set(this.payoutCopyFlash, '__roster__', true)
        setTimeout(() => { this.$delete(this.payoutCopyFlash, '__roster__') }, 1600)
      } catch (e) {
        // eslint-disable-next-line no-alert
        window.prompt('Copy:', text)
      }
    },
    downloadPhotoshootRosterCsv () {
      const esc = v => {
        const s = String(v == null ? '' : v)
        return /[",\n]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s
      }
      const header = ['Dancer Name', 'Dancer Email', 'Indoor Slots', 'Outdoor Slots', 'Total Shots', 'Raw Proofs']
      const rows = this.photoshootRoster.map(r => [
        r.dancerName,
        r.dancerEmail,
        r.indoorSlots,
        r.outdoorSlots,
        r.totalShots,
        r.rawProofs ? 'Yes' : ''
      ])
      const csv = [header, ...rows].map(row => row.map(esc).join(',')).join('\n')
      const blob = new Blob([csv], { type: 'text/csv' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = 'iabf-2026-photoshoot-roster.csv'
      a.click()
      URL.revokeObjectURL(url)
    },
    async copyPayoutLine (p) {
      const text = `${p.name}: $${(p.collectedCents / 100).toFixed(2)} (${p.sessions} session${p.sessions === 1 ? '' : 's'})`
      try {
        await navigator.clipboard.writeText(text)
        this.$set(this.payoutCopyFlash, p.name, true)
        setTimeout(() => { this.$delete(this.payoutCopyFlash, p.name) }, 1600)
      } catch (e) {
        // eslint-disable-next-line no-alert
        window.prompt('Copy:', text)
      }
    },
    async copyPayoutList () {
      const lines = this.payouts.map(p =>
        `${p.name}: $${(p.collectedCents / 100).toFixed(2)} (${p.sessions} session${p.sessions === 1 ? '' : 's'})`
      )
      const text = lines.join('\n')
      try {
        await navigator.clipboard.writeText(text)
        this.$set(this.payoutCopyFlash, '__all__', true)
        setTimeout(() => { this.$delete(this.payoutCopyFlash, '__all__') }, 1600)
      } catch (e) {
        // eslint-disable-next-line no-alert
        window.prompt('Copy:', text)
      }
    },
    async confirmDelete (row) {
      // eslint-disable-next-line no-alert
      if (!window.confirm(`Delete this signup?\n${row.dancerName} · ${row.serviceLabel} · $${((row.netCents || 0) / 100).toFixed(2)}`)) return
      try {
        await db.collection(this.IABF.addonServices).doc(row.id).delete()
      } catch (e) {
        // eslint-disable-next-line no-alert
        window.alert('Delete failed: ' + (e.message || e))
      }
    },
    openCsvImport () {
      this.csvService = ''
      this.csvVariant = 'indoor-1'
      this.csvExtraPhotos = 0
      this.csvRawProofs = false
      this.csvText = ''
      this.csvRows = []
      this.csvError = ''
      this.showCsv = true
    },
    // Loose name matcher: input like "Phyllis M", "Anne WB", "Sakia
    // Bryson" → find a dancer whose first name matches AND whose last
    // name either matches, starts with, or has the pasted letters as
    // its word-initials (so "WB" resolves to "Witteman Borowitz" or
    // "Witteman-Borowitz").
    matchDancerLoose (rawName) {
      // Normalise: strip periods on initials ("Ana G." → "Ana G"),
      // collapse whitespace, lowercase for comparison.
      const clean = String(rawName || '')
        .replace(/\./g, '')
        .replace(/\s+/g, ' ')
        .trim()
      if (!clean) return { dancer: null, error: 'empty name' }
      const words = clean.split(' ').filter(Boolean)
      if (!words.length) return { dancer: null, error: 'empty name' }

      const matchesLast = (ln, restIn, restCompact) => {
        if (!ln) return false
        if (ln === restIn) return true
        if (ln.startsWith(restIn)) return true
        const initials = ln.split(/[\s-]+/).filter(Boolean).map(w => w[0] || '').join('')
        return initials === restCompact
      }

      // Try every split point (first N words = firstName, remainder =
      // lastName pattern). Handles compound first names like "Rosa Mary"
      // whose dancer record stores "Rosa Mary" in the firstName field.
      const hits = []
      for (let split = 1; split <= words.length; split++) {
        const firstIn = words.slice(0, split).join(' ').toLowerCase()
        const restIn = words.slice(split).join(' ').toLowerCase().trim()
        const restCompact = restIn.replace(/[\s-]+/g, '')
        const cands = (this.dancers || []).filter(d =>
          (d.firstName || '').toLowerCase().trim() === firstIn
        )
        if (!cands.length) continue
        const matches = restIn === ''
          ? cands
          : cands.filter(d => matchesLast((d.lastName || '').toLowerCase().trim(), restIn, restCompact))
        for (const m of matches) {
          if (!hits.includes(m)) hits.push(m)
        }
      }
      if (hits.length === 1) return { dancer: hits[0], error: '' }
      if (hits.length === 0) return { dancer: null, error: `no dancer matched "${clean}"` }
      return { dancer: null, error: `${hits.length} dancers match "${clean}" — needs more letters` }
    },
    parseCsvPreview () {
      this.csvError = ''
      this.csvRows = []
      if (!this.csvService) { this.csvError = 'Pick a service first.'; return }
      const text = String(this.csvText || '').replace(/^﻿/, '').trim()
      if (!text) { this.csvError = 'Paste at least one name.'; return }
      const lines = text.split(/\r?\n/).map(l => l.trim()).filter(Boolean)
      // Track credits consumed within this preview + already in the DB,
      // split by gift type so a dancer with multiple gifts consumes
      // each at most once.
      const consumedByDancer = {}
      const ensureConsumed = id => {
        if (!consumedByDancer[id]) consumedByDancer[id] = { standard: 0, fivePhoto: 0, carpool: 0 }
        return consumedByDancer[id]
      }
      for (const r of this.rows) {
        if (r.giftCreditCents > 0 && r.dancerId) {
          const c = ensureConsumed(r.dancerId)
          if (r.serviceType === 'photoshoot') {
            if (r.giftExtraPhotos > 0) c.fivePhoto += 1
            else c.standard += 1
          } else if (r.serviceType === 'carpool') {
            c.carpool += carpoolCreditsUsedByRow(r)
          }
        }
      }
      const parsed = lines.map(raw => {
        const row = { raw, valid: true, error: '', teacher: '' }
        // Private lessons: separate teacher and student. Accept any of:
        //   "Teacher | Student"     (pipe)
        //   "Teacher\tStudent"      (tab, e.g. copy-paste from Sheets)
        //   "Teacher   Student"     (2+ consecutive spaces)
        let namePart = raw
        if (this.csvService === 'privateLesson') {
          let parts = null
          if (raw.includes('|')) parts = raw.split('|')
          else if (raw.includes('\t')) parts = raw.split('\t')
          else {
            // Fall back to 2+ consecutive spaces as the column boundary
            // so users can paste tab-collapsed rows from Google Sheets.
            const m = raw.split(/\s{2,}/)
            if (m.length >= 2) parts = m
          }
          if (!parts || parts.length < 2 || !parts[0].trim() || !parts[1].trim()) {
            row.valid = false
            row.error = 'expected "Teacher | Student" (or 2+ spaces / tab between)'
            return row
          }
          row.teacher = parts[0].trim()
          namePart = parts[1].trim()
        }
        const m = this.matchDancerLoose(namePart)
        if (!m.dancer) { row.valid = false; row.error = m.error; return row }
        const dancer = m.dancer
        row.dancerId = dancer.id
        row.dancerName = `${dancer.firstName || ''} ${dancer.lastName || ''}`.trim()
        row.dancerEmail = (dancer.email || '').toLowerCase().trim()
        row.serviceType = this.csvService
        row.serviceLabel = SERVICE_INFO[this.csvService].label
        let basePriceCents = 0
        if (this.csvService === 'photoshoot') {
          const v = PHOTOSHOOT_VARIANTS[this.csvVariant]
          if (!v) { row.valid = false; row.error = 'unknown variant'; return row }
          row.variant = this.csvVariant
          row.extraPhotos = Math.max(0, Number(this.csvExtraPhotos) || 0)
          row.rawProofs = !!this.csvRawProofs
          basePriceCents = v.basePriceCents +
            row.extraPhotos * EXTRA_PHOTO_CENTS +
            (row.rawProofs ? RAW_PROOFS_CENTS : 0)
          const giftCounts = countGifts(dancer)
          const c = ensureConsumed(dancer.id)
          const pick = pickGift(giftCounts, c)
          row.giftCreditCents = pick.type ? Math.min(GIFT_CREDIT_CENTS, basePriceCents) : 0
          row.giftExtraPhotos = pick.extraPhotos
          if (pick.type === 'fivePhoto') c.fivePhoto += 1
          else if (pick.type === 'standard') c.standard += 1
        } else if (this.csvService === 'carpool') {
          const v = CARPOOL_VARIANTS[this.csvCarpoolVariant]
          if (!v) { row.valid = false; row.error = 'unknown carpool route'; return row }
          row.variant = this.csvCarpoolVariant
          basePriceCents = v.priceCents
          // 1 gift = 1 free MCO leg or full DAB; 2 gifts = full MCO roundtrip.
          const giftCounts = countGifts(dancer)
          const c = ensureConsumed(dancer.id)
          const remaining = Math.max(0, giftCounts.carpool - c.carpool)
          const applied = carpoolAddonGiftCredit(this.csvCarpoolVariant, basePriceCents, remaining)
          row.giftCreditCents = applied.giftCreditCents
          c.carpool += applied.creditsUsed
          row.extraPhotos = 0
          row.rawProofs = false
        } else {
          basePriceCents = SERVICE_INFO[this.csvService].basePriceCents
          row.variant = ''
          row.extraPhotos = 0
          row.rawProofs = false
          row.giftCreditCents = 0
        }
        row.basePriceCents = basePriceCents
        row.netCents = Math.max(0, basePriceCents - row.giftCreditCents)
        row.optionsLabel = this.optionsLabel(row)
        return row
      })
      this.csvRows = parsed
    },
    toggleAllCarpoolImport (checked) {
      for (const r of this.carpoolImportRows) r.selected = !!checked
    },
    buildPnl (serviceType, spendCents) {
      // Charged = sum of netCents across all statuses (this is what
      // dancers actually pay after gifts). Gift value = sum of
      // giftCreditCents (what she "gave away" vs sticker price).
      // Net = Charged - Spent (Julie's out-of-pocket cost).
      let chargedCents = 0
      let giftCents = 0
      let signupCount = 0
      for (const r of this.rows) {
        if (r.serviceType !== serviceType) continue
        chargedCents += Number(r.netCents) || 0
        giftCents += Number(r.giftCreditCents) || 0
        signupCount += 1
      }
      const spent = Math.max(0, Number(spendCents) || 0)
      return {
        signupCount,
        chargedCents,
        giftCents,
        spendCents: spent,
        netCents: chargedCents - spent
      }
    },
    onPnlSpendInput (kind, raw) {
      // Live-update the draft as the user types; only persist on blur.
      this.$set(this.pnlSpendDraft, kind, raw)
    },
    async savePnlSpend (kind) {
      const raw = this.pnlSpendDraft[kind]
      const dollars = Number(raw)
      if (!isFinite(dollars) || dollars < 0) {
        // Bad input — revert to the stored value.
        this.$set(this.pnlSpendDraft, kind, this.pnlSpendDollarsFor(kind))
        return
      }
      const cents = Math.round(dollars * 100)
      const field = kind === 'photoshoot' ? 'photoshootSpendCents' : 'carpoolSpendCents'
      this.$set(this.pnl, 'saving', kind)
      try {
        await db.collection(this.IABF.config).doc('addon-services-pnl').set({
          [field]: cents
        }, { merge: true })
        this.$set(this.pnl, 'savedFlash', kind)
        setTimeout(() => { if (this.pnl.savedFlash === kind) this.$set(this.pnl, 'savedFlash', null) }, 1200)
      } catch (e) {
        console.error('Save P&L spend failed:', e)
        // eslint-disable-next-line no-alert
        window.alert('Save failed: ' + (e.message || e))
      }
      this.$set(this.pnl, 'saving', null)
    },
    pnlSpendDollarsFor (kind) {
      const cents = kind === 'photoshoot' ? this.pnl.photoshootSpendCents : this.pnl.carpoolSpendCents
      return cents ? (cents / 100).toFixed(2) : ''
    },
    async openCarpoolImport () {
      this.showCarpoolImport = true
      this.carpoolImportRows = []
      this.carpoolImportError = ''
      this.carpoolImportLoading = true
      try {
        const snap = await db.collection(this.IABF.carpools).get()
        const slots = snap.docs.map(d => ({ id: d.id, ...d.data() }))
        // Skip waitlist rows and any admin-disabled slots. Confirmed
        // riders only.
        const active = slots.filter(s => !s.isWaitlist && s.active !== false)

        // Group each dancer's arrival + departure slot so we can pick
        // the right variant (roundtrip if both same airport, otherwise
        // two separate one-way rows).
        const byDancer = {}
        for (const slot of active) {
          const direction = slot.direction === 'departure' ? 'departure' : 'arrival'
          const airport = String(slot.airport || '').toUpperCase()
          if (!['MCO', 'DAB'].includes(airport)) continue
          for (const rider of (slot.dancers || [])) {
            if (!rider || rider.isGuest) continue
            if (!rider.id || String(rider.id).startsWith('guest-')) continue
            const dancer = this.dancerById[rider.id]
            if (!dancer) continue
            if (!byDancer[rider.id]) byDancer[rider.id] = { dancer }
            byDancer[rider.id][direction] = {
              slotId: slot.id,
              airport,
              date: slot.date || '',
              time: slot.time || ''
            }
          }
        }

        // Any carpool rows already in the addon-services table — used
        // to skip re-imports so this button is safe to re-run.
        const existingKeys = new Set()
        for (const r of this.rows) {
          if (r.serviceType === 'carpool' && r.dancerId && r.variant) {
            existingKeys.add(`${r.dancerId}|${r.variant}`)
          }
        }

        const rows = []
        for (const dancerId of Object.keys(byDancer)) {
          const g = byDancer[dancerId]
          const dancer = g.dancer
          const emit = (variantKey, note) => {
            if (existingKeys.has(`${dancerId}|${variantKey}`)) return
            const v = CARPOOL_VARIANTS[variantKey]
            if (!v) return
            rows.push({
              key: `${dancerId}|${variantKey}`,
              dancerId,
              dancer,
              dancerName: `${dancer.firstName || ''} ${dancer.lastName || ''}`.trim(),
              dancerEmail: (dancer.email || '').toLowerCase(),
              variantKey,
              variantLabel: v.label,
              giftEligible: v.giftEligible,
              basePriceCents: v.priceCents,
              giftCreditCents: 0, // filled below
              netCents: v.priceCents,
              note,
              selected: true
            })
          }
          const a = g.arrival
          const d = g.departure
          if (a && d && a.airport === d.airport) {
            emit(`${a.airport.toLowerCase()}-roundtrip`, `Arr ${a.date} ${a.time} · Dep ${d.date} ${d.time}`)
          } else if (a && d) {
            emit(`${a.airport.toLowerCase()}-arrival`, `Arr ${a.date} ${a.time} · ${a.airport}`)
            emit(`${d.airport.toLowerCase()}-departure`, `Dep ${d.date} ${d.time} · ${d.airport}`)
          } else if (a) {
            emit(`${a.airport.toLowerCase()}-arrival`, `Arr ${a.date} ${a.time} · ${a.airport}`)
          } else if (d) {
            emit(`${d.airport.toLowerCase()}-departure`, `Dep ${d.date} ${d.time} · ${d.airport}`)
          }
        }

        // Apply carpool gifts. Seed "consumed" from existing paid /
        // pending carpool rows so a dancer can't double-claim if she
        // already has some manually-added carpool signups on the table.
        // Credits: 1-way gift = 1, legacy roundtrip gift = 2; MCO RT needs 2.
        const consumed = {}
        for (const r of this.rows) {
          if (r.serviceType === 'carpool' && r.giftCreditCents > 0 && r.dancerId) {
            consumed[r.dancerId] = (consumed[r.dancerId] || 0) + carpoolCreditsUsedByRow(r)
          }
        }
        for (const row of rows) {
          const giftCounts = countGifts(row.dancer)
          const used = consumed[row.dancerId] || 0
          const remaining = Math.max(0, giftCounts.carpool - used)
          if (row.giftEligible && remaining > 0) {
            const applied = carpoolAddonGiftCredit(row.variant, row.basePriceCents, remaining)
            row.giftCreditCents = applied.giftCreditCents
            consumed[row.dancerId] = used + applied.creditsUsed
          }
          row.netCents = Math.max(0, row.basePriceCents - row.giftCreditCents)
        }

        rows.sort((a, b) => {
          const an = `${a.dancer.lastName || ''} ${a.dancer.firstName || ''}`
          const bn = `${b.dancer.lastName || ''} ${b.dancer.firstName || ''}`
          return an.localeCompare(bn)
        })
        this.carpoolImportRows = rows
      } catch (e) {
        console.error('Carpool import scan failed:', e)
        this.carpoolImportError = e.message || String(e)
      }
      this.carpoolImportLoading = false
    },
    async submitCarpoolImport () {
      const selected = this.carpoolImportRows.filter(r => r.selected)
      if (!selected.length) return
      this.carpoolImporting = true
      try {
        for (const r of selected) {
          await db.collection(this.IABF.addonServices).add({
            dancerId: r.dancerId,
            dancerName: r.dancerName,
            dancerEmail: r.dancerEmail,
            serviceType: 'carpool',
            serviceLabel: 'Airport Carpool',
            teacher: '',
            variant: r.variantKey,
            extraPhotos: 0,
            rawProofs: false,
            basePriceCents: r.basePriceCents,
            giftCreditCents: r.giftCreditCents,
            giftExtraPhotos: 0,
            netCents: r.netCents,
            status: 'pending',
            notes: r.note || '',
            optionsLabel: r.variantLabel + (r.note ? ' · ' + r.note : ''),
            createdAt: new Date(),
            source: 'carpool-import'
          })
        }
        this.showCarpoolImport = false
      } catch (e) {
        console.error('Carpool import failed:', e)
        this.carpoolImportError = e.message || String(e)
      }
      this.carpoolImporting = false
    },
    async submitCsv () {
      const valid = this.csvRows.filter(r => r.valid)
      if (!valid.length) return
      this.csvImporting = true
      try {
        for (const r of valid) {
          await db.collection(this.IABF.addonServices).add({
            dancerId: r.dancerId,
            dancerName: r.dancerName,
            dancerEmail: r.dancerEmail,
            serviceType: r.serviceType,
            serviceLabel: r.serviceLabel,
            teacher: r.teacher || '',
            variant: r.variant || '',
            extraPhotos: r.extraPhotos || 0,
            rawProofs: !!r.rawProofs,
            basePriceCents: r.basePriceCents,
            giftCreditCents: r.giftCreditCents || 0,
            giftExtraPhotos: r.giftExtraPhotos || 0,
            netCents: r.netCents,
            status: 'pending',
            notes: r.notes || '',
            optionsLabel: r.optionsLabel || (r.teacher ? 'w/ ' + r.teacher : ''),
            createdAt: new Date(),
            source: 'csv-import'
          })
        }
        this.showCsv = false
      } catch (e) {
        console.error('CSV import failed:', e)
        this.csvError = e.message || 'Import failed.'
      }
      this.csvImporting = false
    }
  }
}
</script>

<style scoped>
.as-head { display: flex; justify-content: space-between; align-items: baseline; gap: 12px; margin-bottom: 4px; }
.as-head h2 { font-family: Quattrocento, Georgia, serif; font-size: 26px; margin: 0; }
.as-head-actions { display: flex; gap: 8px; }
.muted { color: #888; }
.small { font-size: 12px; }
.pos { color: #2e7d32; }

.as-summary { display: flex; gap: 12px; margin: 16px 0 8px; }
.as-stat { background: #faf7f0; border: 1px solid #e6e1d4; border-radius: 6px; padding: 10px 18px; min-width: 120px; text-align: center; }
.as-stat-num { font-family: 'Fanwood Text', Georgia, serif; font-size: 22px; font-weight: 700; color: #1a1a1a; }
.as-stat-lab { font-size: 11px; text-transform: uppercase; letter-spacing: 0.05em; color: #555; margin-top: 2px; }

.as-bulk { display: flex; gap: 12px; align-items: center; margin-bottom: 12px; }
.as-filters { display: flex; gap: 16px; margin: 12px 0; font-size: 13px; }
.as-filters label { display: flex; flex-direction: column; gap: 3px; color: #555; }

.as-input, .as-input-sm { padding: 5px 8px; border: 1px solid #ccc; border-radius: 3px; background: #fff; font-size: 13px; font-family: inherit; }
.as-input { width: 100%; }
.as-input-sm { width: 70px; }
.as-inline { display: inline-flex; align-items: center; gap: 6px; margin-right: 16px; }
.as-field { margin: 10px 0; }
.as-field label { display: block; font-size: 12px; font-weight: 700; color: #555; margin-bottom: 3px; text-transform: uppercase; letter-spacing: 0.04em; }
.as-field-row { display: flex; gap: 20px; align-items: center; flex-wrap: wrap; margin: 10px 0; }
.as-summary-mini { margin-top: 14px; padding: 10px 14px; background: #f5f3f0; border-radius: 4px; font-size: 13px; }
.as-summary-mini > div { padding: 2px 0; }

.as-table { width: 100%; border-collapse: collapse; font-size: 13px; }
.as-table th { text-align: left; padding: 8px; background: #faf8f3; font-size: 11px; text-transform: uppercase; color: #777; border-bottom: 1px solid #e6e1d4; }
.as-table td { padding: 8px; border-bottom: 1px solid #f3f1ed; vertical-align: middle; }
.as-table td.num, .as-table th.num { text-align: right; font-family: 'Fanwood Text', Georgia, serif; }
.as-table td.opts { max-width: 260px; font-size: 12px; }
.as-table td.notes { max-width: 220px; }
.as-table tr.row-paid td { background: #f4fbf5; }
.as-table tr.row-failed td { background: #fdecec; }
.as-table tr.dancer-header td {
  background: #f5f3ec;
  border-top: 2px solid #d8d3c2;
  padding: 10px 8px;
  font-size: 13px;
}
.as-table tr.dancer-header td:first-child strong { font-family: Quattrocento, Georgia, serif; font-size: 15px; }
.as-table tr.dancer-sub td:first-child { border-left: 3px solid #f5f3ec; }
.as-table tr.dancer-sub td { font-size: 12px; }
.as-table.sm td, .as-table.sm th { padding: 5px 6px; font-size: 12px; }

.status { padding: 1px 8px; border-radius: 10px; font-size: 11px; font-weight: 700; }
.status-pending { background: #fff3cd; color: #856404; }
.status-paid { background: #d4edda; color: #155724; }
.status-failed { background: #f8d7da; color: #721c24; }
.status-invoiced { background: #d1ecf1; color: #0c5460; }

.as-csv { width: 100%; min-height: 200px; padding: 10px 12px; border: 1px solid #ccc; border-radius: 4px; font-family: ui-monospace, Menlo, monospace; font-size: 12px; }
.as-csv-preview { margin-top: 12px; }
.as-csv-preview h4 { font-family: Quattrocento, Georgia, serif; font-size: 16px; margin: 0 0 6px; }
.row-invalid td { background: #f8d7da33; color: #a83232; }
.row-5photo td { background: #fff0f4; }

.as-payouts {
  margin: 12px 0 18px;
  padding: 12px 16px;
  background: #f4faf7;
  border: 1px solid #b6dcbf;
  border-radius: 6px;
}
.as-pnl {
  margin: 12px 0 18px;
  padding: 12px 16px;
  background: #f7f5ef;
  border: 1px solid #d8d3c2;
  border-radius: 6px;
}
.as-pnl > summary { cursor: pointer; padding: 2px 0; }
.pnl-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(280px, 1fr)); gap: 16px; margin-top: 12px; }
.pnl-card { background: #fff; border: 1px solid #e6e1d4; border-radius: 6px; padding: 12px 16px; }
.pnl-title { font-family: Quattrocento, Georgia, serif; font-size: 15px; font-weight: 700; margin-bottom: 8px; color: #1a1a1a; }
.pnl-row { display: flex; justify-content: space-between; align-items: center; padding: 4px 0; font-size: 13px; }
.pnl-row-muted { color: #888; }
.pnl-row-input { padding: 8px 0; border-top: 1px solid #f0ebda; border-bottom: 1px solid #f0ebda; margin: 4px 0; }
.pnl-row-input label { font-weight: 700; font-size: 13px; color: #555; }
.pnl-input-wrap { display: flex; align-items: center; gap: 4px; }
.pnl-dollar { color: #666; font-family: 'Fanwood Text', Georgia, serif; }
.pnl-input {
  width: 90px; padding: 4px 6px;
  border: 1px solid #ccc; border-radius: 3px;
  font-family: 'Fanwood Text', Georgia, serif;
  font-size: 14px; text-align: right;
}
.pnl-flash { color: #2e7d32; font-weight: 700; font-size: 12px; }
.pnl-row-total { padding: 6px 0 2px; font-size: 15px; }
.pnl-row-total.pos strong { color: #155724; }
.pnl-row-total.neg strong { color: #721c24; }

.as-roster {
  margin: 12px 0 18px;
  padding: 12px 16px;
  background: #fef5f7;
  border: 1px solid #f0c8d3;
  border-radius: 6px;
}
.as-roster > summary {
  cursor: pointer;
  padding: 2px 0;
}
.as-roster p { margin: 6px 0 8px; }
.roster-list {
  columns: 2;
  column-gap: 24px;
  margin: 0 0 12px;
  padding-left: 20px;
  font-size: 13px;
  line-height: 1.55;
}
.roster-list li { break-inside: avoid; margin-bottom: 2px; }
.roster-actions { display: flex; gap: 8px; }
.as-payouts > summary {
  cursor: pointer;
  padding: 2px 0;
}
.as-payouts p { margin: 6px 0 8px; }
.payout-totals td {
  border-top: 2px solid #b6dcbf;
  font-family: 'Fanwood Text', Georgia, serif;
}
.badge-5photo {
  display: inline-block;
  background: #dd4762;
  color: #fff;
  font-weight: 700;
  font-size: 11px;
  padding: 2px 8px;
  border-radius: 10px;
  letter-spacing: 0.02em;
  margin-right: 6px;
  vertical-align: middle;
}
</style>
